import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';

interface Tenant {
  id: number; slug: string; company_name: string; admin_email: string; status: string;
  app_version: string | null; api_port: number | null;
  stripe_customer_id: string | null; stripe_subscription_id: string | null;
  activated_at: string | null; created_at: string;
  suspended_at: string | null; cancelled_at: string | null;
  metadata: Record<string, unknown>;
  product_name: string | null; product_slug: string | null;
}
interface Job {
  id: number; job_type: string; status: string; attempts: number;
  last_error: string | null; created_at: string; completed_at: string | null;
}
interface StripeSummary {
  has_stripe: boolean;
  subscription?: {
    id: string; status: string; cancel_at_period_end: boolean;
    current_period_end: number; trial_end: number | null;
  } | null;
  recent_charges?: Array<{
    id: string; amount: number; currency: string; status: string;
    refunded: boolean; amount_refunded: number; created: number; receipt_url: string | null;
  }>;
}
interface AuditRow {
  id: string; actor_email: string; action: string;
  details: unknown; created_at: string;
}
interface Health {
  slug: string;
  tenant_status: string;
  pm2: { status: string | null; pid: number | null; restarts: number | null;
         uptime_ms: number | null; cpu: number | null; mem_bytes: number | null;
         _error?: string };
  db:  { size_bytes: number | null; _error?: string };
  api: { status_code: number | null; latency_ms: number | null; _error?: string };
  backup: { last_success_at: string | null; last_attempt_at: string | null;
            last_status: string | null; last_size_bytes: number | null;
            last_error: string | null };
}
interface Logs {
  slug: string;
  lines: string[];
  pm2_stderr?: string;
  pm2_exit: number;
}

export function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState<{ tenant: Tenant; recent_jobs: Job[] } | null>(null);
  const [audit, setAudit] = useState<AuditRow[] | null>(null);
  const [stripe, setStripe] = useState<StripeSummary | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [logs, setLogs] = useState<Logs | null>(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsBusy, setLogsBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await api<{ tenant: Tenant; recent_jobs: Job[] }>(`/admin/tenants/${id}`);
      setData(d);
      const s = await api<StripeSummary>(`/admin/tenants/${id}/stripe-summary`).catch(() => ({ has_stripe: false }) as StripeSummary);
      setStripe(s);
      // Best-effort audit fetch. Non-fatal on failure — the rest of
      // the page still renders.
      api<{ events: AuditRow[] }>(`/admin/audit?target_type=tenant&target_id=${id}&limit=25`)
        .then((a) => setAudit(a.events))
        .catch(() => setAudit([]));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Health auto-refresh every 10s. Independent of the main load() so that
  // a slow probe doesn't delay the rest of the page.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    async function pull() {
      try {
        const h = await api<Health>(`/admin/tenants/${id}/health`);
        if (!cancelled) setHealth(h);
      } catch {
        // ignore — leave previous reading visible rather than blanking on transient failure
      }
    }
    pull();
    const iv = setInterval(pull, 10_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [id]);

  async function loadLogs(lines = 100) {
    setLogsBusy(true);
    try {
      const l = await api<Logs>(`/admin/tenants/${id}/logs?lines=${lines}`);
      setLogs(l);
    } catch (e) {
      flash(`Logs: ${(e as Error).message}`, true);
    } finally {
      setLogsBusy(false);
    }
  }

  function flash(msg: string, isErr = false) {
    if (isErr) { setError(msg); setTimeout(() => setError(null), 6000); }
    else       { setInfo(msg);  setTimeout(() => setInfo(null), 4000); }
  }

  async function action(label: string, path: string, body: unknown = {}) {
    setBusy(true);
    try {
      const r = await api<{ ok?: boolean; enqueued?: string; sent_to?: string; status?: string; refund_id?: string }>(
        path, { method: 'POST', json: body }
      );
      flash(`${label}: ${r.enqueued ? `queued (${r.enqueued})` : r.sent_to ? `sent to ${r.sent_to}` : r.refund_id ? `refund ${r.refund_id}` : 'OK'}`);
      load();
    } catch (e) {
      flash(`${label}: ${(e as Error).message}`, true);
    } finally {
      setBusy(false);
    }
  }

  // Holds the URL after a successful mint so we can render a clickable
  // fallback link if Chrome's popup blocker ate window.open().
  const [opLoginUrl, setOpLoginUrl] = useState<string | null>(null);

  async function operatorLogin() {
    setBusy(true);
    setOpLoginUrl(null);
    // CRITICAL: open the tab SYNCHRONOUSLY inside the click handler.
    // Chrome (and Safari, FF) only honors window.open() during a live user
    // gesture. The await below pops us out of that gesture window, so a
    // window.open() AFTER the await is silently popup-blocked — the
    // operator sees "link issued" but no tab appears, and clicks the
    // button a second time wondering what happened.
    //
    // The fix is the standard "synchronous pre-open" pattern: open about:blank
    // immediately, then redirect that tab once the mint completes. If the
    // mint fails or the tab was closed, we fall back to rendering a
    // clickable link in the flash banner.
    const popup = window.open('about:blank', '_blank', 'noopener');
    try {
      const r = await api<{ url: string; expires_in_seconds: number;
        signing_in_as: { id: number; email: string; username: string | null } }>(
        `/admin/tenants/${id}/operator-login`,
        { method: 'POST', json: {} },
      );
      if (popup && !popup.closed) {
        popup.location.href = r.url;
        flash(`Operator login link issued for ${r.signing_in_as.email} (15-min TTL)`);
      } else {
        // Popup blocker bit us. Stash the URL so we can render a manual link.
        setOpLoginUrl(r.url);
        flash(
          `Operator login link ready for ${r.signing_in_as.email} — click the link below (popup was blocked)`,
          true,
        );
      }
    } catch (e) {
      // Close the about:blank tab we opened pre-flight so we don't leak it.
      if (popup && !popup.closed) popup.close();
      flash(`Operator login: ${(e as Error).message}`, true);
    } finally {
      setBusy(false);
    }
  }

  async function destroyTenant() {
    if (!data) return;
    const confirmation = prompt(
      `This will PERMANENTLY DELETE the database, code, and all data for "${data.tenant.slug}". Type the slug to confirm:`
    );
    if (confirmation !== data.tenant.slug) {
      flash('confirmation mismatch — cancelled', true);
      return;
    }
    await action('Destroy', `/admin/tenants/${id}/destroy`, { confirm_slug: data.tenant.slug });
    setTimeout(() => navigate('/'), 1500);
  }

  if (!data) {
    return (
      <div>
        <p><Link to="/">← Tenants</Link></p>
        {error ? <div className="banner err">{error}</div> : <div className="muted">Loading…</div>}
      </div>
    );
  }

  const t = data.tenant;
  const statusPill =
    t.status === 'active' ? 'ok' :
    t.status === 'failed' || t.status === 'cancelled' ? 'err' :
    t.status === 'suspended' ? 'warn' : 'muted';

  return (
    <div>
      <p><Link to="/">← Tenants</Link></p>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h1 style={{ flex: 1 }}>{t.slug}</h1>
        <span className={`pill ${statusPill}`}>{t.status}</span>
      </div>
      <p className="subtitle">
        {t.company_name} · {t.admin_email} ·{' '}
        <a href={`https://${t.slug}.dealerbosses.com`} target="_blank" rel="noreferrer">
          {t.slug}.dealerbosses.com ↗
        </a>
      </p>
      {error && <div className="banner err">{error}</div>}
      {info && <div className="banner ok">{info}</div>}

      <div className="card">
        <h3>Actions</h3>
        <div className="button-row" style={{ marginTop: 0 }}>
          <button disabled={busy} onClick={() => action('Resend magic link', `/admin/tenants/${id}/resend-magic-link`)}>
            ✉ Resend magic link
          </button>
          {t.status === 'failed' && (
            <button disabled={busy} onClick={() => action('Retry provision', `/admin/tenants/${id}/retry`)}>
              ↻ Retry provision
            </button>
          )}
          {(t.status === 'active' || t.status === 'suspended') && (
            <button className="secondary" disabled={busy}
              onClick={() => action('Refresh quotas', `/admin/tenants/${id}/refresh-quotas`)}
              title="Recompute quotas from current product + push to tenant API">
              ⟳ Refresh quotas
            </button>
          )}
          {(t.status === 'active' || t.status === 'trialing' || t.status === 'past_due') && (
            <button className="secondary" disabled={busy}
              onClick={() => action('Update app', `/admin/tenants/${id}/update`)}
              title="Re-rsync the current customer-app template into this tenant + pm2 restart. Brief downtime (a few seconds) during restart.">
              ⇧ Update app
            </button>
          )}
          {t.api_port && (
            <button className="secondary" disabled={busy}
              onClick={() => action('Backup now', `/admin/tenants/${id}/backup-now`)}
              title="Queue an on-demand pg_dump of this tenant's DB. Writes to /var/backups/dealerbosses/on-demand/ and updates the Last backup health box.">
              💾 Backup now
            </button>
          )}
          {/* Excluded 'suspended' — PM2 is stopped and Caddy vhost is gone,
              so the minted URL would hit a 404 / cert error. Unsuspend first. */}
          {(t.status === 'active' || t.status === 'trialing' || t.status === 'past_due') && (
            <button className="secondary" disabled={busy}
              onClick={() => operatorLogin()}
              title="Mint a 15-min JWT for this tenant's admin user and open the tenant SPA logged in. Audit-logged. For support: see exactly what the customer sees.">
              🎭 Sign in as tenant admin
            </button>
          )}
          {opLoginUrl && (
            <div className="banner" style={{ marginTop: 8 }}>
              Popup blocked.{' '}
              <a href={opLoginUrl} target="_blank" rel="noopener noreferrer">
                Click here to sign in as tenant admin
              </a>
              {' '}(link expires in 15 minutes).
            </div>
          )}
          {t.status === 'active' && (
            <button className="secondary" disabled={busy} onClick={() => action('Suspend', `/admin/tenants/${id}/suspend`)}>
              ⏸ Suspend (stop PM2 + remove vhost)
            </button>
          )}
          {t.status === 'suspended' && (
            <button disabled={busy} onClick={() => action('Unsuspend', `/admin/tenants/${id}/unsuspend`)}>
              ▶ Unsuspend
            </button>
          )}
          {t.stripe_subscription_id && t.status !== 'cancelled' && (
            <>
              <button className="secondary" disabled={busy} onClick={() => action('Cancel at period end', `/admin/tenants/${id}/cancel-subscription`)}>
                Cancel sub at period end
              </button>
              <button className="danger" disabled={busy} onClick={() => action('Cancel immediately', `/admin/tenants/${id}/cancel-subscription`, { immediate: true })}>
                Cancel sub immediately
              </button>
            </>
          )}
          {t.stripe_customer_id && (
            <button className="secondary" disabled={busy} onClick={() => action('Refund last', `/admin/tenants/${id}/refund-last`)}>
              ↩ Refund last charge
            </button>
          )}
          <div className="spacer" />
          <button className="danger" disabled={busy} onClick={destroyTenant}>
            🗑 Destroy (irreversible)
          </button>
        </div>
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Health
          <span className="muted small" style={{ fontWeight: 'normal' }}>(auto-refresh every 10s)</span>
        </h3>
        {!health ? <div className="muted">Probing…</div> : (
          <div className="form-row" style={{ alignItems: 'start', gap: 16 }}>
            <HealthBox
              title="PM2 process"
              pill={health.pm2.status === 'online' ? 'ok'
                  : health.pm2.status === 'not-found' || health.pm2.status === null ? 'err'
                  : 'warn'}
              pillText={health.pm2.status ?? 'unknown'}
              rows={[
                ['PID',       health.pm2.pid != null ? String(health.pm2.pid) : '—'],
                ['Restarts',  health.pm2.restarts != null ? String(health.pm2.restarts) : '—'],
                ['Uptime',    health.pm2.uptime_ms != null ? humanDuration(health.pm2.uptime_ms) : '—'],
                ['CPU',       health.pm2.cpu != null ? `${health.pm2.cpu}%` : '—'],
                ['Memory',    health.pm2.mem_bytes != null ? humanBytes(health.pm2.mem_bytes) : '—'],
              ]}
              error={health.pm2._error}
            />
            <HealthBox
              title="Database"
              pill={health.db.size_bytes != null ? 'ok' : 'err'}
              pillText={health.db.size_bytes != null ? 'reachable' : 'unreachable'}
              rows={[
                ['Size',      health.db.size_bytes != null ? humanBytes(health.db.size_bytes) : '—'],
              ]}
              error={health.db._error}
            />
            <HealthBox
              title="API endpoint"
              pill={health.api.status_code != null && health.api.status_code < 400 ? 'ok'
                  : health.api.status_code != null ? 'warn' : 'err'}
              pillText={health.api.status_code != null ? `HTTP ${health.api.status_code}` : 'no response'}
              rows={[
                ['Latency',   health.api.latency_ms != null ? `${health.api.latency_ms} ms` : '—'],
              ]}
              error={health.api._error}
            />
            <HealthBox
              title="Last backup"
              pill={backupPill(health.backup)}
              pillText={backupPillText(health.backup)}
              rows={[
                ['Succeeded', health.backup.last_success_at
                    ? `${humanDuration(Date.now() - Date.parse(health.backup.last_success_at))} ago`
                    : 'never'],
                ['Size',      health.backup.last_size_bytes != null
                    ? humanBytes(health.backup.last_size_bytes) : '—'],
                ['Attempt',   health.backup.last_attempt_at
                    ? new Date(health.backup.last_attempt_at).toLocaleString() : '—'],
              ]}
              error={health.backup.last_error ?? undefined}
            />
          </div>
        )}
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          Logs
          <button className="small secondary" style={{ marginLeft: 'auto' }}
                  onClick={() => { setLogsOpen((o) => !o); if (!logsOpen && !logs) loadLogs(); }}>
            {logsOpen ? 'Hide' : 'Show'}
          </button>
          {logsOpen && (
            <button className="small" disabled={logsBusy} onClick={() => loadLogs()} title="Refetch last 100 lines">
              {logsBusy ? '…' : '⟳ Refresh'}
            </button>
          )}
        </h3>
        {logsOpen && (
          !logs ? (logsBusy ? <div className="muted">Loading…</div> : <div className="muted">No logs yet.</div>)
          : logs.lines.length === 0 ? (
              <div className="muted">
                No log output found for <code>tenant-{logs.slug}</code>.
                {logs.pm2_stderr && <pre style={{ fontSize: 11, marginTop: 8 }}>{logs.pm2_stderr}</pre>}
              </div>
          ) : (
            <pre style={{ fontSize: 11, background: 'var(--code-bg)', padding: 12, borderRadius: 4,
                          maxHeight: 360, overflow: 'auto', whiteSpace: 'pre', margin: 0 }}>
              {logs.lines.join('\n')}
            </pre>
          )
        )}
      </div>

      <div className="form-row" style={{ alignItems: 'start' }}>
        <div className="card">
          <h3>Workspace details</h3>
          <table style={{ width: '100%', fontSize: 13 }}>
            <tbody>
              <Row k="Plan" v={`${t.product_name ?? '—'} (${t.product_slug ?? '?'})`} />
              <Row k="App version" v={t.app_version ?? '—'} mono />
              <Row k="API port" v={t.api_port ? String(t.api_port) : '—'} mono />
              <Row k="Created" v={new Date(t.created_at).toLocaleString()} />
              <Row k="Activated" v={t.activated_at ? new Date(t.activated_at).toLocaleString() : '—'} />
              {t.suspended_at && <Row k="Suspended" v={new Date(t.suspended_at).toLocaleString()} />}
              {t.cancelled_at && <Row k="Cancelled" v={new Date(t.cancelled_at).toLocaleString()} />}
              <Row k="Stripe customer" v={t.stripe_customer_id ?? '—'} mono />
              <Row k="Stripe sub" v={t.stripe_subscription_id ?? '—'} mono />
            </tbody>
          </table>
        </div>
        <div className="card">
          <h3>Stripe (live)</h3>
          {!stripe ? <div className="muted">Loading…</div>
          : !stripe.has_stripe ? <div className="muted">No Stripe customer on record.</div>
          : (
            <>
              {stripe.subscription ? (
                <table style={{ width: '100%', fontSize: 13 }}>
                  <tbody>
                    <Row k="Status" v={stripe.subscription.status} />
                    <Row k="Cancels at period end?" v={stripe.subscription.cancel_at_period_end ? 'yes' : 'no'} />
                    <Row k="Period ends" v={new Date(stripe.subscription.current_period_end * 1000).toLocaleString()} />
                    {stripe.subscription.trial_end && (
                      <Row k="Trial ends" v={new Date(stripe.subscription.trial_end * 1000).toLocaleString()} />
                    )}
                  </tbody>
                </table>
              ) : <div className="muted">No active subscription.</div>}
              {stripe.recent_charges && stripe.recent_charges.length > 0 && (
                <>
                  <h4 style={{ marginTop: 16, fontSize: 14 }}>Recent charges</h4>
                  <table style={{ width: '100%', fontSize: 12 }}>
                    <tbody>
                      {stripe.recent_charges.map((c) => (
                        <tr key={c.id} style={{ borderBottom: '1px solid var(--border)' }}>
                          <td style={{ padding: '4px 0' }}>{new Date(c.created * 1000).toLocaleDateString()}</td>
                          <td>{(c.amount/100).toLocaleString('en-CA', {style:'currency', currency:c.currency.toUpperCase()})}</td>
                          <td><span className={`pill ${c.status === 'succeeded' ? 'ok' : 'err'}`}>{c.status}</span></td>
                          <td>
                            {c.refunded && <span className="pill muted">refunded</span>}
                            {c.receipt_url && <a href={c.receipt_url} target="_blank" rel="noreferrer">receipt</a>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <div className="card">
        <h3>Recent provisioning jobs</h3>
        {data.recent_jobs.length === 0 ? (
          <div className="muted">No jobs yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th>Type</th><th>Status</th><th>Attempts</th><th>Created</th><th>Last error</th>
              </tr>
            </thead>
            <tbody>
              {data.recent_jobs.map((j) => (
                <tr key={j.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td>{j.job_type}</td>
                  <td>{j.status}</td>
                  <td>{j.attempts}</td>
                  <td>{new Date(j.created_at).toLocaleString()}</td>
                  <td className="error-text" style={{ maxWidth: 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={j.last_error ?? ''}>
                    {j.last_error?.split('\n')[0] ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card">
        <h3 style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Activity
          <Link to={`/audit?target_type=tenant&target_id=${t.id}`} className="muted small" style={{ textDecoration: 'none' }}>
            Open in audit log →
          </Link>
        </h3>
        {audit === null ? (
          <div className="muted">Loading…</div>
        ) : audit.length === 0 ? (
          <div className="muted">No admin actions logged for this tenant yet.</div>
        ) : (
          <table style={{ width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th>When</th><th>Actor</th><th>Action</th><th>Details</th>
              </tr>
            </thead>
            <tbody>
              {audit.map((a) => {
                const danger = a.action.endsWith('.destroy')
                  || a.action.endsWith('.refund-last')
                  || a.action.endsWith('.cancel-subscription');
                return (
                  <tr key={a.id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                      {new Date(a.created_at).toLocaleString()}
                    </td>
                    <td>{a.actor_email}</td>
                    <td>
                      <span className={`pill ${danger ? 'err' : 'muted'}`}>{a.action}</span>
                    </td>
                    <td className="code small" style={{ maxWidth: 360, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.details ? JSON.stringify(a.details) : <span className="muted">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {Object.keys(t.metadata ?? {}).length > 0 && (
        <div className="card">
          <h3>Metadata</h3>
          <pre style={{ fontSize: 12, background: 'var(--code-bg)', padding: 12, borderRadius: 4, overflow: 'auto' }}>
            {JSON.stringify(t.metadata, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function Row({ k, v, mono }: { k: string; v: string; mono?: boolean }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--border)' }}>
      <td style={{ padding: '6px 0', color: 'var(--muted)', width: '40%' }}>{k}</td>
      <td className={mono ? 'code' : ''} style={{ wordBreak: 'break-all' }}>{v}</td>
    </tr>
  );
}

function HealthBox({ title, pill, pillText, rows, error }: {
  title: string;
  pill: 'ok' | 'warn' | 'err';
  pillText: string;
  rows: Array<[string, string]>;
  error?: string;
}) {
  return (
    <div style={{ flex: 1, minWidth: 200 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <span className={`pill ${pill}`} style={{ fontSize: 11 }}>{pillText}</span>
      </div>
      <table style={{ width: '100%', fontSize: 12 }}>
        <tbody>
          {rows.map(([k, v]) => (
            <tr key={k}>
              <td style={{ color: 'var(--muted)', padding: '2px 0', width: '45%' }}>{k}</td>
              <td className="code">{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {error && <div className="error-text small" style={{ marginTop: 4, fontSize: 11 }} title={error}>{error.slice(0, 80)}</div>}
    </div>
  );
}

function backupPill(b: Health['backup']): 'ok' | 'warn' | 'err' {
  if (!b.last_success_at) return 'err';
  // Stale if last success > 36h ago (cron runs daily — gives 12h grace).
  const ageMs = Date.now() - Date.parse(b.last_success_at);
  if (ageMs > 36 * 3600_000) return 'warn';
  if (b.last_status !== 'success') return 'warn'; // last attempt failed
  return 'ok';
}
function backupPillText(b: Health['backup']): string {
  if (!b.last_success_at) return 'never';
  if (b.last_status === 'failed') return 'last attempt failed';
  const ageMs = Date.now() - Date.parse(b.last_success_at);
  if (ageMs > 36 * 3600_000) return 'stale';
  return 'fresh';
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : v >= 10 ? 1 : 2)} ${units[i]}`;
}

function humanDuration(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}
